/**
 * Minimal interface for items that need take ID generation.
 * Both assets and jobs implement this shape.
 */
export interface TakeIdItem {
  id: string
  sceneId: string | null
  parentId: string | null
  siblingIndex: number
}

/**
 * Generate a stable take ID for an item.
 *
 * Take IDs are used to:
 * - Group related shots (takes + variations) together
 * - Store take customizations (names, descriptions)
 * - Enable group drag operations on the canvas
 *
 * Format:
 * - Root items (no parent): `root-{sceneId}-{siblingIndex}` (scoped per scene)
 * - Project-level items (no scene): `root-project-{siblingIndex}` (scoped globally)
 * - Child items (has parent): `child-{itemId}` (unique to the extension/variation)
 *
 * @param parentId - The parent asset/job ID, or null for root items
 * @param siblingIndex - The take number within the scene (for roots) or parent (for children)
 * @param itemId - The unique ID of this asset or job
 * @param sceneId - The scene this item belongs to, or null for project-level assets
 */
export function getTakeId(
  parentId: string | null,
  siblingIndex: number,
  itemId: string,
  sceneId: string | null,
): string {
  const effectiveSceneId = sceneId ?? 'project'
  return parentId === null ? `root-${effectiveSceneId}-${siblingIndex}` : `child-${itemId}`
}

/**
 * Generate a take ID from an item that has the required fields.
 * Convenience wrapper around getTakeId for items with known shape.
 */
export function getTakeIdFromItem(item: TakeIdItem): string {
  return getTakeId(item.parentId, item.siblingIndex, item.id, item.sceneId ?? null)
}
